import { isNonNullish } from '@epicuro-next/utilities';
import {
  BasketColumn,
  DateBasketColumn,
  PriceBasketColumn,
  QuantityBasketColumn,
} from 'graphql-api/baskets-api-interfaces';

import { Mappings } from '../mappings/basket.models';

import { isDtoBackendUnavailable } from './is-dto-backend-unavailable';
import { isDtoBasketChecked } from './is-dto-basket-checked';
import { isDtoBasketPlaced } from './is-dto-basket-placed';

export function getColumnRequestedValue(column?: BasketColumn) {
  return (
    column &&
    ((column as DateBasketColumn).date ??
      (column as QuantityBasketColumn | PriceBasketColumn).value?.toString())
  );
}

export function getColumnConfirmedValue(column?: BasketColumn) {
  return column
    ? (column as DateBasketColumn).actualDate ??
        (column as QuantityBasketColumn | PriceBasketColumn).actual?.toString()
    : null;
}

export function mapColumnPrimaryValue(
  column: BasketColumn,
  basketIsChecked: boolean,
  backendNotAvailable: boolean,
  showConfirmedValues: boolean,
) {
  const requestedValue = getColumnRequestedValue(column);
  const confirmedValue = getColumnConfirmedValue(column);

  if (basketIsChecked || showConfirmedValues) {
    if (backendNotAvailable) {
      return requestedValue;
    }

    if (isNonNullish(confirmedValue)) {
      return confirmedValue;
    }
  }

  return requestedValue;
}

export function mapColumnSecondaryValue(
  column: BasketColumn,
  basketIsChecked: boolean,
  backendNotAvailable: boolean,
  showConfirmedValues: boolean,
  columnsShowRequestedOnPlaced?: string[],
) {
  const requestedValue = getColumnRequestedValue(column);
  const confirmedValue = getColumnConfirmedValue(column);
  const showValueOnPlaced = columnsShowRequestedOnPlaced?.includes(column.key);

  return (basketIsChecked || (showConfirmedValues && showValueOnPlaced)) &&
    !backendNotAvailable &&
    confirmedValue !== undefined &&
    requestedValue !== confirmedValue
    ? requestedValue
    : null;
}

export function mapColumnDisplayValues(
  column: BasketColumn,
  basketIsChecked: boolean,
  backendNotAvailable: boolean,
  showConfirmedValues: boolean,
  columnsShowRequestedOnPlaced?: string[],
): BasketColumn {
  return {
    ...column,
    primaryValue: mapColumnPrimaryValue(
      column,
      basketIsChecked,
      backendNotAvailable,
      showConfirmedValues,
    ),
    secondaryValue: mapColumnSecondaryValue(
      column,
      basketIsChecked,
      backendNotAvailable,
      showConfirmedValues,
      columnsShowRequestedOnPlaced,
    ),
  };
}

export function mapColumnsDisplayValues(
  columns: BasketColumn[],
  basketIsChecked: boolean,
  backendNotAvailable: boolean,
  showConfirmedValues: boolean,
  columnsShowRequestedOnPlaced?: string[],
): BasketColumn[] {
  return columns.map((column) =>
    mapColumnDisplayValues(
      column,
      basketIsChecked,
      backendNotAvailable,
      showConfirmedValues,
      columnsShowRequestedOnPlaced,
    ),
  );
}

export function mapColumns(columns: BasketColumn[], maps: Mappings) {
  const basketIsChecked = isDtoBasketChecked(maps.basket);
  const backendNotAvailable = isDtoBackendUnavailable(maps.basket);
  const isBasketPlaced = isDtoBasketPlaced(maps.basket);
  const columnsShowRequestedOnPlaced =
    maps.settings.columnsShowRequestedOnPlaced;

  return mapColumnsDisplayValues(
    columns,
    basketIsChecked,
    backendNotAvailable,
    isBasketPlaced,
    columnsShowRequestedOnPlaced,
  );
}
